import clsx from "clsx";
import { useScroll } from "framer-motion";
import { useMotionValueEvent } from "framer-motion";
import React, { FC, useEffect, useRef, useState } from "react";

import { useBreakpoints } from "../../../hooks/use-breakpoints";
import { CommonColors } from "../../../types/colors";
import { Fade } from "../../animations/fade";
import { Cta } from "../../buttons/cta";
import { ContentfulImage } from "../../contentful-image";
import { AnimatedSliderInner } from "./animated-slider-inner";
import { colorScheme } from "./color-scheme";
import { AnimatedSliderLayout, AnimatedSliderState } from "./types";

export type AnimatedSliderItemData =
  | Queries.FlatCardDataFragment
  | Queries.ImageCardDataFragment
  | Queries.MaterialCardDataFragment
  | Queries.AppendixCardDataFragment
  | (Queries.ProductDataFragment & { __typename: "ContentfulProduct" })
  | (Queries.WhatsNextDataFragment & { __typename: "ContentfulWhatsNext" });

export type AnimatedSliderVariant = CommonColors;

export interface AnimatedSliderProps {
  /**
   * Slider logo on left / top
   */
  logo?: Queries.ContentfulImageDataFragment | null;
  /**
   * Slider title on left / top
   */
  title?: string;
  /**
   * Slider description, shown before animation and on mobile
   */
  description?: string;
  /**
   * Description visibility across devices
   */
  headerLayout?: AnimatedSliderLayout;
  /**
   * Slider CTA button on left / top-right
   */
  cta?: Queries.CtaDataFragment | null;
  /**
   * Slider items (uses `FlatCard` or `ImageCard` component)
   */
  items: AnimatedSliderItemData[];
  /**
   * Whether to start with grouped card layout and animate
   */
  initialAnimation?: boolean;
  /**
   * Background variant
   */
  variant: AnimatedSliderVariant;
  /**
   * Number of visible cards
   */
  maxVisibleCards: number | null;
}

export const AnimatedSlider: FC<AnimatedSliderProps> = ({
  items,
  logo,
  title,
  description,
  headerLayout,
  cta,
  maxVisibleCards,
  initialAnimation = false,
  variant,
}) => {
  // Forbid initial Animation in mobile;
  const { xs: bpInit, lg: isDesktop } = useBreakpoints();
  const [animationState, setAnimationState] = useState<AnimatedSliderState | null>(null);

  useEffect(() => {
    setAnimationState((state) => {
      if (state === null && bpInit) {
        return initialAnimation && isDesktop
          ? AnimatedSliderState.INITIAL
          : AnimatedSliderState.FINAL;
      } else {
        return state;
      }
    });
  }, [initialAnimation, isDesktop, bpInit]);

  const rootRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ axis: "y", target: rootRef });

  useMotionValueEvent(scrollYProgress, "change", (value) => {
    if (animationState === AnimatedSliderState.INITIAL && value > 0.3) {
      setAnimationState(AnimatedSliderState.TRANSITION);
    }
  });

  const isFinalState = animationState === AnimatedSliderState.FINAL;

  const ctaElement = cta && (
    <Cta {...cta} variant={colorScheme[variant].ctaVariant ?? CommonColors.Primary} />
  );

  return (
    <div ref={rootRef}>
      {animationState != null ? (
        <div
          className={clsx("w-full relative", {
            "lg:h-[150vh]": initialAnimation,
          })}
        >
          <div
            className={clsx(
              {
                "sticky top-0 min-h-[100vh]": initialAnimation,
              },
              colorScheme[variant].containerClassName,
              "w-full overflow-hidden",
              "flex flex-col items-start justify-center",
              "px-8 py-9 lg:px-20 lg:py-10"
            )}
          >
            {isFinalState && logo && (
              <ContentfulImage
                image={logo}
                className={clsx(
                  "max-w-full mb-10",
                  headerLayout === AnimatedSliderLayout.Horizontal && "[&_img]:w-fit max-h-32",
                  headerLayout === AnimatedSliderLayout.Vertical && "max-h-36"
                )}
                gatsbyImageProps={{ objectFit: "contain" }}
              />
            )}
            <div
              className={clsx(
                "flex flex-col w-full justify-between items-start md:items-center mb-8 gap-6 transition-all",
                headerLayout === AnimatedSliderLayout.Horizontal && "md:flex-row",
                !isFinalState && "opacity-0 !h-0 !mb-0"
              )}
            >
              <h2
                className={clsx(
                  "text-4xl 2xl:text-6xl font-medium md:font-bold",
                  colorScheme[variant].titleClassName
                )}
              >
                {title}
              </h2>
              <div
                className={clsx(
                  "text-lg xl:text-xl 2xl:text-2xl",
                  headerLayout === AnimatedSliderLayout.Horizontal && "md:hidden",
                  colorScheme[variant].descriptionClassName
                )}
              >
                {description}
              </div>
              <div className="hidden md:block">{ctaElement}</div>
            </div>
            <Fade
              className={clsx(
                "absolute top-0 bottom-0 -z-10 lg:z-10 hidden",
                "lg:flex flex-col gap-10 h-full justify-center items-start w-[30%]"
              )}
              visible={animationState === AnimatedSliderState.INITIAL}
              duration={0.6}
              additional={{ initial: { opacity: 1 }, exit: { x: -1000, opacity: 1 } }}
            >
              <div className="flex flex-col gap-8 w-full relative">
                {logo && (
                  <ContentfulImage
                    image={logo}
                    className="max-h-36 max-w-full [&_img]:w-fit [&_img]:max-w-full"
                    gatsbyImageProps={{ objectFit: "contain" }}
                  />
                )}
                <h2
                  className={clsx(
                    "font-medium",
                    logo ? "text-6xl" : "text-[90px] leading-[100px]",
                    colorScheme[variant].titleClassName
                  )}
                >
                  {title}
                </h2>
              </div>
              <p className={clsx("text-lg font-light", colorScheme[variant].descriptionClassName)}>
                {description}
              </p>
              {ctaElement}
            </Fade>
            <AnimatedSliderInner
              {...{ animationState, items, variant, maxVisibleCards }}
              onAnimationComplete={() =>
                setAnimationState((curr) =>
                  curr === AnimatedSliderState.TRANSITION ? AnimatedSliderState.FINAL : curr
                )
              }
              isMobile={!isDesktop}
            />
            <div className="md:hidden flex justify-center w-full mt-3">{ctaElement}</div>
          </div>
        </div>
      ) : null}
    </div>
  );
};
